
const libPictProvider = require('pict-provider');
const libPictRouter = require('pict-router');

class PlatformConfigurationRouter extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.currentView = 'unknown';
		// Settings (the full configuration browser) is the landing.
		this.defaultRoute = 'Settings';
		this.cachedLogoutRoute = false;

		// Monotonic navigation token for the "latest-wins" render guard. Every route
		// change (app + PSRS) advances it via the before-hook; each dispatched view is
		// stamped with the token current at dispatch, and a view skips its DOM
		// projection if the token has advanced by the time its async data resolves.
		// This stops slow, still-in-flight dashboard renders from flashing over (or
		// landing on top of) the screen the user actually navigated to last.
		this._routeToken = 0;

		/** @type {any} */
		this.pict;

		if (!('PictRouter' in this.pict.providers))
		{
			// SkipRouteResolveOnAdd ensures the router does NOT resolve the current
			// URL as each route is registered. Without it, the first route added
			// ('/') resolves immediately and navigates to the default Dashboard view
			// before the route the user actually deep-linked to has been registered,
			// causing the initial route to load ahead of the intended one. The app
			// performs a single explicit resolve once every route (including the PSRS
			// routes) is registered after login.
			this.pict.addProvider('PictRouter', { SkipRouteResolveOnAdd: true }, libPictRouter);
		}

		// Reusable "Dashboard Mode": any top-level view that sets `DashboardMode: true` in its configuration renders
		// edge-to-edge at the full browser width (no content gutters). The before-hook clears the mode on every
		// navigation and _renderRouteView re-applies it for dashboard views, so it is self-cleaning. See _applyDashboardMode.
		if (this.pict && this.pict.CSSMap && (typeof this.pict.CSSMap.addCSS === 'function'))
		{
			this.pict.CSSMap.addCSS('PlatformConfig-DashboardMode-CSS', _DASHBOARD_MODE_CSS, 400);
		}

		this.router.hooks(
		{
			before: async (next, params) =>
			{
				if (!this._confirmLeavingApprovalWorkflowEdits())
				{
					return next(false);
				}
				// Advance the navigation token for EVERY route change (including PSRS
				// routes, which are handled by the RecordSetRouter) so any view render
				// already in flight can detect it has been superseded — see _routeToken.
				this._routeToken++;
				// Dashboard Mode is off by default; _renderRouteView re-enables it for a DashboardMode view. Clearing here
				// (on every navigation, including routes that bypass _renderRouteView like the workspace) keeps it from lingering.
				this._applyDashboardMode(false);
				if (params?.url == 'Logout')
				{
					return next();
				}
				// Safety net: make sure AppData.CurrentUser is populated before a route
				// handler runs (handlers and the profile chrome read it). Only fetches
				// once — after login CurrentUser is already set, so normal navigation
				// does not incur a round-trip.
				if (!this.AppData.CurrentUser)
				{
					await new Promise((resolve) =>
					{
						this.pict.RestClient.getJSON('/1.0/CheckSession', (pError, pResponse, pBody) =>
						{
							if (pBody?.LoggedIn)
							{
								this.AppData.CurrentUser = pBody;
							}
							resolve();
						});
					});
				}
				// Route-aware help: if the documentation panel is open, point it at the
				// topic mapped to the new route (no-op while the panel is closed).
				if (this.pict.PictApplication && typeof this.pict.PictApplication.updateHelpForRoute === 'function')
				{
					this.pict.PictApplication.updateHelpForRoute();
				}
				// keep the docked navigation rail's active
				// highlight in step with the current route (no-op for page styles).
				if (this.pict.PictApplication && typeof this.pict.PictApplication.syncNavSidebarActive === 'function')
				{
					this.pict.PictApplication.syncNavSidebarActive();
				}
				// Keep the "editing the template for <type>" return banner in step with the route.
				if (this.pict.PictApplication && typeof this.pict.PictApplication.syncTemplateContextBanner === 'function')
				{
					this.pict.PictApplication.syncTemplateContextBanner();
				}
				next();
			},
		});
	}

	get pictRouter()
	{
		return this.pict.providers.PictRouter;
	}

	get router()
	{
		return this.pictRouter.router;
	}

	/**
	 * Resolve the route currently in the browser hash, falling back to the
	 * default route when the hash is empty. Call this once after every route
	 * (application + PSRS) is registered so deep-links land on their intended
	 * view without first rendering the default route.
	 */
	resolveCurrentRoute()
	{
		if (!this.pictRouter.navigateCurrent())
		{
			if (this.cachedLogoutRoute)
			{
				const tmpCachedRoute = this.cachedLogoutRoute;
				this.cachedLogoutRoute = false;
				this.router.navigate(tmpCachedRoute);
			}
			else
			{
				this.router.navigate(this.defaultRoute);
			}
		}
		// If a raced navigation strands the boot splash (nothing painted the container), re-resolve.
		this._armBootSplashWatchdog();
	}

	/**
	 * Ask before navigation throws away unsaved approval workflow edits. The hash has already changed when the router's
	 * before-hook runs, so declining puts it back without firing another route, which would reload over the edits.
	 *
	 * @return {boolean} true if navigation may proceed
	 */
	_confirmLeavingApprovalWorkflowEdits()
	{
		const tmpProvider = this.pict.providers.ApprovalWorkflow;
		const tmpView = this.pict.views.ApprovalWorkflowEditor;
		if ((this.currentView !== 'ApprovalWorkflowEditor') || !tmpProvider || !(tmpProvider.hasUnsavedEdits() || (tmpView && tmpView.hasPendingPanelEdits())))
		{
			return true;
		}
		if (window.confirm('This approval workflow has unsaved changes. Leave without saving them?'))
		{
			tmpProvider.state.Editor.Dirty = false;
			return true;
		}
		const tmpEditor = tmpProvider.state.Editor;
		const tmpProjectPath = tmpEditor.IDProject ? `/Project/${tmpEditor.IDProject}` : '';
		window.history.replaceState(null, '', tmpEditor.IsNew ? '#/ApprovalWorkflow/New' : `#/ApprovalWorkflow/${encodeURIComponent(tmpEditor.Hash)}${tmpProjectPath}`);
		return false;
	}

	/**
	 * Render a routed view with the latest-wins guard applied. Stamps the view with the
	 * navigation token current at dispatch so a slower, earlier render drops its paint if
	 * the user has navigated again before its data resolved. Use this from every route
	 * handler instead of calling the view's renderAsync directly.
	 *
	 * @param {string} pViewIdentifier - The view to render into the application container.
	 */
	_renderRouteView(pViewIdentifier)
	{
		const tmpView = this.pict.views[pViewIdentifier];
		if (!tmpView)
		{
			this.pict.log.warn(`Route view not found: ${pViewIdentifier}`);
			return;
		}
		// Full-browser-width Dashboard Mode when this view opts in (config `DashboardMode: true`).
		this._applyDashboardMode(!!(tmpView.options && tmpView.options.DashboardMode));
		this._installLatestWinsGuard(tmpView);
		// Stamp with the token set by the before-hook for THIS navigation.
		tmpView._platformConfigRenderToken = this._routeToken;
		tmpView.renderAsync((pRenderError) =>
		{
			if (pRenderError)
			{
				this.pict.log.error(`Error rendering ${pViewIdentifier} view: ${pRenderError}`);
			}
		});
	}

	/**
	 * Toggle the app-shell "Dashboard Mode" — a reusable full-browser-width layout for dashboard screens. When on, the
	 * `.app-wrapper-container.is-dashboard` CSS drops the content gutters + lifts the `.container` max-width so the active
	 * view spans edge to edge. Opt in per view with `DashboardMode: true`; the before-hook clears it on every navigation.
	 * @param {boolean} pOn
	 */
	_applyDashboardMode(pOn)
	{
		try
		{
			if (typeof document === 'undefined') { return; }
			const tmpWrapper = document.querySelector('.app-wrapper-container');
			if (tmpWrapper) { tmpWrapper.classList.toggle('is-dashboard', !!pOn); }
		}
		catch (pError) { /* best-effort — layout falls back to the standard gutters */ }
	}

	/**
	 * Idempotently wrap a view's onProjectAsync so it skips the DOM projection when a newer
	 * navigation has advanced the token since this render started. The async work (data
	 * fetch in onBeforeRenderAsync) still completes; only the paint is dropped, so a stale
	 * dashboard never flashes over the current one. Chart building in onAfterRender is
	 * already canvas-null-safe, so a dropped paint leaves no orphaned charts.
	 *
	 * @param {any} pView
	 */
	_installLatestWinsGuard(pView)
	{
		if (pView._platformConfigLatestWinsGuarded)
		{
			return;
		}
		pView._platformConfigLatestWinsGuarded = true;
		const tmpRouter = this;
		const fOriginalOnProjectAsync = pView.onProjectAsync.bind(pView);
		pView.onProjectAsync = function (fCallback, pRenderable)
		{
			if (pView._platformConfigRenderToken !== tmpRouter._routeToken)
			{
				// Superseded by a newer navigation — normally drop this stale paint to avoid a stale
				// view flashing over the current one. BUT if the boot splash is still up, nothing else
				// has painted the container yet: dropping here strands the "Loading…" spinner forever
				// (the classic infinite-spinner — a raced navigation, e.g. a PSRS list Search+Apply pair
				// or the login double-cycle restoring a list route, advances the token before this first
				// paint lands). A slightly-stale first paint is strictly better than a dead spinner, so
				// paint anyway while the splash is present; mid-session (no splash) the latest-wins drop
				// still stands.
				if (!tmpRouter._bootSplashPresent())
				{
					return fCallback();
				}
			}
			return fOriginalOnProjectAsync(fCallback, pRenderable);
		};
	}

	/**
	 * Is the boot "Loading…" splash still occupying the application container? (Shown at boot/login and
	 * only removed when the first routed view paints — so its lingering presence means no view has painted.)
	 * @return {boolean}
	 */
	_bootSplashPresent()
	{
		if (typeof document === 'undefined') { return false; }
		const tmpContainer = document.querySelector('#PlatformConfiguration-Application-Container');
		if (!tmpContainer) { return false; }
		const tmpSplash = tmpContainer.querySelector('.is-loading-splash');
		return !!(tmpSplash && !tmpSplash.classList.contains('is-hidden'));
	}

	/**
	 * Backstop for the infinite spinner: after resolving a route, if the boot splash is STILL up a beat
	 * later, a paint was never dispatched (not just dropped) — re-resolve the current hash once (fresh
	 * token, no competing navigation this time) so it paints. Bounded retries; disarms as soon as the
	 * splash clears. No-op on every healthy boot (the splash is already gone).
	 */
	_armBootSplashWatchdog()
	{
		if (typeof setTimeout === 'undefined') { return; }
		if (this._bootSplashTimer) { clearTimeout(this._bootSplashTimer); }
		this._bootSplashTimer = setTimeout(() =>
		{
			this._bootSplashTimer = null;
			if (!this._bootSplashPresent())
			{
				// Healthy boot: a view painted over the splash. Reset the retry counter and clear the
				// one-reload guard so a FUTURE stuck boot can recover again.
				this._bootSplashRetries = 0;
				try { if ((typeof window !== 'undefined') && window.sessionStorage) { window.sessionStorage.removeItem('PCApp-BootSplashReloaded'); } } catch (pError) { /* best effort */ }
				return;
			}
			this._bootSplashRetries = (this._bootSplashRetries || 0) + 1;
			if (this._bootSplashRetries <= 2)
			{
				// A raced/never-dispatched first paint left the splash up. Re-resolve the current hash
				// (fresh token; the guard above now lets a paint land while the splash is present).
				this.pict.log.warn(`Boot splash still up after route resolve — re-resolving the current route (attempt ${this._bootSplashRetries}).`);
				this.pictRouter.navigateCurrent();
				this._armBootSplashWatchdog();
				return;
			}
			// Last resort: re-resolves couldn't clear it (e.g. a paint that never dispatched, or a view
			// the app router's guard doesn't own). A stuck boot splash means nothing is in progress to
			// lose, so hard-reload ONCE to recover — guarded by sessionStorage so it can never loop.
			try
			{
				if ((typeof window !== 'undefined') && window.sessionStorage && (window.sessionStorage.getItem('PCApp-BootSplashReloaded') === '1'))
				{
					this.pict.log.error('Boot splash still stuck after a recovery reload — not reloading again (avoiding a loop).');
					return;
				}
				if ((typeof window !== 'undefined') && window.sessionStorage) { window.sessionStorage.setItem('PCApp-BootSplashReloaded', '1'); }
				this.pict.log.error('Boot splash stuck after re-resolve retries — hard-reloading once to recover.');
				if ((typeof window !== 'undefined') && window.location && (typeof window.location.reload === 'function')) { window.location.reload(); }
			}
			catch (pError) { /* best effort */ }
		}, 900);
	}

	onBeforeLoadDataAsync(fCallback)
	{
		super.onBeforeLoadDataAsync((pError) =>
		{
			this.pictRouter.addRoute('/',
			(pRouteData) =>
			{
				if (this.pict.PictApplication.loggedIn)
				{
					if (this.cachedLogoutRoute)
					{
						const tmpCachedRoute = this.cachedLogoutRoute;
						this.cachedLogoutRoute = false;
						this.router.navigate(tmpCachedRoute);
					}
					else
					{
						this.router.navigate(this.defaultRoute);
					}
				}
				else
				{
					this.pict.views.Empty.renderAsync();
				}
			});

			this.pictRouter.addRoute('/Logout',
			(pRouteData) =>
			{
				// The Logout link is `href="#/Logout" onclick="logout()"`, so this route resolves while the
				// real logout (Deauthenticate -> loginAsync) is in flight, and again — critically — when the
				// user logs back in: onAfterLoginAsync -> resolveCurrentRoute() re-resolves whatever hash is
				// still in the bar, which is #/Logout. This handler renders nothing, so without the redirect
				// below the user lands on a blank screen after re-login.
				//
				// Remember where they were so a re-login returns them there, then: if the session is live
				// (the pre-Deauthenticate instant OR the post-re-login resolve), bounce off the empty Logout
				// route to that cached route (or the default). While genuinely logged out, do nothing — the
				// login modal that onLoginAsync shows owns the screen.
				const tmpCurrentRoute = this.router.lastResolved();
				if (tmpCurrentRoute && tmpCurrentRoute.length > 0)
				{
					const tmpHash = tmpCurrentRoute[0].hashString;
					if (tmpHash && (tmpHash !== '/Logout'))
					{
						this.cachedLogoutRoute = tmpHash;
					}
				}
				if (this.pict.PictApplication.loggedIn)
				{
					const tmpTarget = this.cachedLogoutRoute || this.defaultRoute;
					this.cachedLogoutRoute = false;
					this.router.navigate(tmpTarget);
				}
			});

			// The KPI-tile Dashboard view was removed (several of its stats were unreliable). Keep the route
			// as a redirect to the configuration home so old bookmarks and the project-clone return link
			// still land somewhere real instead of a dead hash.
			this.pictRouter.addRoute('/Dashboard',
			(pRouteData) =>
			{
				this.router.navigate(this.defaultRoute);
			});

			// Settings = the full configuration browser (every category + the navigation-style chooser) and
			// the landing. #/Configurations stays as an alias so old links keep working. Both render the
			// pict-section-navigation switcher view (falls back to ConfigurationIndex if the nav provider is absent).
			const fRenderSettings = () =>
			{
				this.currentView = 'Settings';
				this._renderRouteView(this.pict.providers['Pict-Navigation'] ? 'ConfigurationNavigation' : 'ConfigurationIndex');
			};
			this.pictRouter.addRoute('/Settings', fRenderSettings);
			this.pictRouter.addRoute('/Configurations', fRenderSettings);

			// top-level header destinations — scoped topic pages. Each sets the current topic (KPI jumbotron
			// + pre-filtered nav scope) and renders TopicPage. (Settings is the catch-all above, not here.)
			const tmpTopics = require('../navigation-topics.js').TOPIC_CONFIGS;
			[ 'Users', 'Projects', 'Observations', 'Documents', 'Database' ].forEach((pKey) =>
			{
				this.pictRouter.addRoute(tmpTopics[pKey].Route.replace(/^#/, ''),
				(pRouteData) =>
				{
					this.currentView = pKey;
					this.pict.AppData.CurrentTopic = tmpTopics[pKey];
					this._renderRouteView(this.pict.providers['Pict-Navigation'] ? 'TopicPage' : 'ConfigurationIndex');
				});
			});

			// RETIRED (destructive): the Contract Administration Setup screen did full-replace Configuration/Put writes
			// against the operator's OWN customer — wiping their config (incl. the whole AppDashboards/HeadLight dashboard
			// set). It is being replaced by a non-destructive per-customer action in the Customer workspace Modules tab.
			// Neutralize it now: redirect any direct hit to the Customers dashboard and never render the destructive view.
			this.pictRouter.addRoute('/ContractAdmin',
			() => { if ((typeof window !== 'undefined') && window.location) { window.location.hash = '#/Customers'; } });

			this.pictRouter.addRoute('/CustomerProvisioning',
			(pRouteData) =>
			{
				this.currentView = 'CustomerProvisioning';
				this._renderRouteView('CustomerProvisioning');
			});

			// Internals version screens — Headlight customer (CustomerID === 1) ONLY. The nav filter hides the
			// entries for other tenants; this route guard also redirects a direct-URL hit to Settings, so the
			// views are genuinely reachable only by Customer 1 users (matching the standalone internals app).
			const fRenderInternalsView = (pViewIdentifier) => () =>
			{
				const tmpApp = this.pict.PictApplication;
				if (!tmpApp || (typeof tmpApp.isHeadlightSession !== 'function') || !tmpApp.isHeadlightSession())
				{
					this.router.navigate(this.defaultRoute);
					return;
				}
				this.currentView = pViewIdentifier;
				this._renderRouteView(pViewIdentifier);
			};
			this.pictRouter.addRoute('/ServiceVersions', fRenderInternalsView('ServiceVersions'));
			this.pictRouter.addRoute('/ServiceVersionsMultiEnv', fRenderInternalsView('ServiceVersionsMultiEnv'));

			this.pictRouter.addRoute('/ProjectClone',
			(pRouteData) =>
			{
				this.currentView = 'ProjectClone';
				this._renderRouteView('ProjectClone');
			});

			this.pictRouter.addRoute('/MasterLists',
			(pRouteData) =>
			{
				this.currentView = 'MasterListManager';
				this._renderRouteView('MasterListManager');
			});

			this.pictRouter.addRoute('/ATMDashboardConfig',
			(pRouteData) =>
			{
				this.currentView = 'ATMDashboardConfig';
				this._renderRouteView('ATMDashboardConfig');
			});
			// Tab (Validation rules / Display columns) as a stable route (the .atm-tab buttons set this hash via
			// ATMDashboardConfig.navigateTab). ScopeMode (Customer/Project) stays in-place — it's a scope filter.
			this.pictRouter.addRoute('/ATMDashboardConfig/:Tab',
			(pRouteData) =>
			{
				const tmpProvider = this.pict.providers.ATMDashboardConfig;
				const tmpTab = (pRouteData.data.Tab === 'display') ? 'display' : 'validation';
				const tmpAlreadyMounted = (this.currentView === 'ATMDashboardConfig');
				this.currentView = 'ATMDashboardConfig';
				if (!tmpAlreadyMounted) { this._renderRouteView('ATMDashboardConfig'); }
				if (tmpProvider) { tmpProvider.setTab(tmpTab); }
			});

			// --- SamplePlan dashboard (net-new pict recreation; a project picker + the shareable dashboard provider) ---
			this.pictRouter.addRoute('/SamplePlanDashboard',
			(pRouteData) =>
			{
				const tmpProvider = this.pict.providers.SamplePlanDashboard;
				if (tmpProvider) { tmpProvider.setRouteProject(0); }
				this.currentView = 'SamplePlanDashboard';
				this._renderRouteView('SamplePlanDashboard');
			});
			this.pictRouter.addRoute('/SamplePlanDashboard/:IDProject',
			(pRouteData) =>
			{
				const tmpProvider = this.pict.providers.SamplePlanDashboard;
				const tmpID = parseInt(pRouteData.data.IDProject, 10) || 0;
				const tmpAlready = (this.currentView === 'SamplePlanDashboard');
				if (tmpProvider) { tmpProvider.setRouteProject(tmpID); }
				this.currentView = 'SamplePlanDashboard';
				// Already on-screen: just open the newly-picked project (avoids a full view re-render + picker remount flash).
				if (tmpAlready && tmpProvider) { tmpProvider.openInto(tmpID, '#SamplePlanDashboard-Host'); }
				else { this._renderRouteView('SamplePlanDashboard'); }
			});
			// Copy one project's sample plan onto another. Deliberately has no menu entry anywhere.
			this.pictRouter.addRoute('/SamplePlan-Copy',
			(pRouteData) =>
			{
				this.currentView = 'SamplePlanCopy';
				this._renderRouteView('SamplePlanCopy');
			});

			// --- Observation list (bespoke, Solr-backed, filterable) — a literal that WINS over the generic
			// pict-section-recordset /PSRS/:RecordSet/List (which hangs scanning the ~21k-row Observations table).
			// Because the app router registers before the RecordSetRouter, this literal shadows the generic list;
			// it is also the workspace Header.ListRoute, so the workspace "back" button lands here (fixes the
			// "route changed but the view stayed" symptom — the fast list actually paints + replaces the detail).
			// One screen, one filter bar, three switchable bodies (List / Charts / Map). Each route selects the initial
			// view mode on the shared ObservationList provider; the in-screen tabs switch without a route change. If the
			// screen is already mounted we refresh in place, otherwise we render the view (its onAfterRender loads it).
			const fRenderObservationList = (pMode) => () =>
			{
				this.currentView = 'ObservationList';
				const tmpProvider = this.pict.providers && this.pict.providers['ObservationList'];
				let tmpMounted = false;
				try { tmpMounted = !!(this.pict.ContentAssignment && this.pict.ContentAssignment.getElement('#ObservationList-Host').length > 0); } catch (pError) { /* */ }
				if (tmpProvider) { tmpProvider._viewMode = pMode || 'list'; }
				if (tmpProvider && tmpMounted && (typeof tmpProvider.loadAsync === 'function')) { tmpProvider.loadAsync(); }
				else { this._renderRouteView('ObservationList'); }
			};
			this.pictRouter.addRoute('/PSRS/Observation/List', fRenderObservationList('list'));
			this.pictRouter.addRoute('/Observations', fRenderObservationList('list'));
			// The Explorer charts + the (next) Map are views OF this same filtered screen, not separate screens.
			this.pictRouter.addRoute('/ObservationExplorer', fRenderObservationList('charts'));
			this.pictRouter.addRoute('/Observations/Explore', fRenderObservationList('charts'));
			this.pictRouter.addRoute('/Observations/Map', fRenderObservationList('map'));

			// --- Documents browse (bespoke, Meadow-backed — no document search index exists) — the document cognate of
			// the Observations screen: one filter bar, List + Charts. Shadows the generic recordset list and takes over
			// the Documents topic route (config screens stay in the nav submenu). Registered before the RecordSetRouter. ---
			const fRenderDocumentList = (pMode) => () =>
			{
				this.currentView = 'DocumentList';
				const tmpProvider = this.pict.providers && this.pict.providers['DocumentList'];
				let tmpMounted = false;
				try { tmpMounted = !!(this.pict.ContentAssignment && this.pict.ContentAssignment.getElement('#DocumentList-Host').length > 0); } catch (pError) { /* */ }
				if (tmpProvider) { tmpProvider._viewMode = (pMode === 'charts') ? 'charts' : 'list'; }
				if (tmpProvider && tmpMounted && (typeof tmpProvider.loadAsync === 'function')) { tmpProvider.loadAsync(); }
				else { this._renderRouteView('DocumentList'); }
			};
			// NOTE: `#/Documents` is a topic route (config menu) registered earlier and wins under first-match, so the
			// browse lives at the generic-list shadow + an explicit browse/charts route (matching how the Observation
			// browse lives at /PSRS/Observation/List + /ObservationExplorer, not at the /Observations topic).
			this.pictRouter.addRoute('/PSRS/Document/List', fRenderDocumentList('list'));
			this.pictRouter.addRoute('/Documents/Browse', fRenderDocumentList('list'));
			this.pictRouter.addRoute('/Documents/Explore', fRenderDocumentList('charts'));

			// --- Inspection Schedule (program-level, bespoke, Meadow-backed) — the agency's Year/Month/Week/Agenda calendar
			// over every inspection. Full-width DashboardMode standalone screen; /:View deep-links a granularity. ---
			const fRenderInspectionSchedule = () => (pRouteData) =>
			{
				this.currentView = 'InspectionSchedule';
				const tmpProvider = this.pict.providers && this.pict.providers['InspectionSchedule'];
				const tmpRouteView = pRouteData && pRouteData.View;
				let tmpMounted = false;
				try { tmpMounted = !!(this.pict.ContentAssignment && this.pict.ContentAssignment.getElement('#InspectionSchedule-Host').length > 0); } catch (pError) { /* */ }
				if (tmpProvider && tmpRouteView && (typeof tmpProvider.applyViewFromRoute === 'function')) { tmpProvider.applyViewFromRoute(tmpRouteView); }
				if (tmpProvider && tmpMounted && (typeof tmpProvider.loadAsync === 'function')) { tmpProvider.loadAsync(); }
				else { this._renderRouteView('InspectionSchedule'); }
			};
			this.pictRouter.addRoute('/InspectionSchedule', fRenderInspectionSchedule());
			this.pictRouter.addRoute('/InspectionSchedule/:View', fRenderInspectionSchedule());

			// --- Document approval THROUGHPUT (reusable, bespoke, Meadow-backed) — submitted/approved-over-time for a
			// document type, from the DocumentApprovalLog transition history. :Type selects the per-type config (defaults
			// to Inspection-Dossier); the same screen serves Structural Concrete etc. by config. ---
			const fRenderDocumentThroughput = () => (pRouteData) =>
			{
				this.currentView = 'DocumentThroughput';
				const tmpView = this.pict.views && this.pict.views['DocumentThroughput'];
				if (tmpView) { tmpView._routeType = (pRouteData && pRouteData.Type) || ''; }
				this._renderRouteView('DocumentThroughput');
			};
			this.pictRouter.addRoute('/DocumentThroughput', fRenderDocumentThroughput());
			this.pictRouter.addRoute('/DocumentThroughput/:Type', fRenderDocumentThroughput());

			// --- Customer Support console — triage/work Support-Call documents under the revolving monthly Support
			// project, with approval-workflow actions + screenshot/file attachments. Full-width DashboardMode screen.
			// Plain addRoute (no gate wrapper): the nav tile is capability-gated for display; the server authorizes. ---
			// Stable routes for every Support view: /Support (Calls), /Support/Metrics|Docs|Settings, /Support/Call/:ID.
			// A WARM route (already mounted + loaded) applies in place — no re-mount, no re-fetch; a COLD one mounts the
			// shell and the provider applies the pending route once loadAsync resolves. See [[config-app-tab-stable-routes]].
			const fSupportRoute = (pApply) =>
			{
				this.currentView = 'Support';
				// Full-browser-width Dashboard Mode on every Support navigation — the before-hook clears `.is-dashboard`
				// each route, and a WARM apply skips _renderRouteView (which is what would otherwise re-apply it).
				this._applyDashboardMode(true);
				const tmpProvider = this.pict.providers && this.pict.providers['Support'];
				let tmpMounted = false;
				try { tmpMounted = !!(this.pict.ContentAssignment && this.pict.ContentAssignment.getElement('#Support-Host').length > 0); } catch (pError) { /* */ }
				if (tmpProvider && tmpMounted && tmpProvider._manifest && !(tmpProvider.state && tmpProvider.state.Loading)) { pApply(tmpProvider); }
				else { if (tmpProvider) { tmpProvider._pendingApply = pApply; } this._renderRouteView('Support'); }
			};
			// Registration order matters: the specific /Support/Call/:ID + /Support/Merge/:IDs come before /Support/:View
			// (so 'Call'/'Merge' aren't captured as a view). /Support/Merge/<id,id,…> deep-links the (deep-linkable) merge wizard.
			this.pictRouter.addRoute('/Support/Call/:ID', (pRouteData) => fSupportRoute((pProvider) => pProvider.routeApplyCall(Number(pRouteData.data.ID))));
			this.pictRouter.addRoute('/Support/Merge/:IDs', (pRouteData) => fSupportRoute((pProvider) => pProvider.routeApplyMerge(String(pRouteData.data.IDs || ''))));
			this.pictRouter.addRoute('/Support/:View', (pRouteData) => fSupportRoute((pProvider) => pProvider.routeApply(String(pRouteData.data.View || '').toLowerCase())));
			this.pictRouter.addRoute('/Support', () => fSupportRoute((pProvider) => pProvider.routeApply('calls')));

			// --- Form Concierge — platform super-users drive the plansheet form pipeline (create / update / build /
			// examples). Same warm/cold _pendingApply pattern as Support. A loaded non-super-user is bounced; before
			// CurrentUser arrives the provider itself fails closed (waits, then shows the locked notice). ---
			const fFormConciergeRoute = (pApply) =>
			{
				const tmpApp = this.pict.PictApplication;
				if (this.AppData.CurrentUser && (!tmpApp || (typeof tmpApp._isSuperUser !== 'function') || !tmpApp._isSuperUser()))
				{
					this.router.navigate(this.defaultRoute);
					return;
				}
				this.currentView = 'FormConcierge';
				const tmpProvider = this.pict.providers && this.pict.providers['FormConcierge'];
				let tmpMounted = false;
				try { tmpMounted = !!(this.pict.ContentAssignment && this.pict.ContentAssignment.getElement('#FormConcierge-Host').length > 0); } catch (pError) { /* */ }
				if (tmpProvider && tmpMounted && tmpProvider._statusLoaded && !(tmpProvider.state && tmpProvider.state.Loading)) { pApply(tmpProvider); }
				else { if (tmpProvider) { tmpProvider._pendingApply = pApply; } this._renderRouteView('FormConcierge'); }
			};
			// :Flow is lower-cased and whitelisted; anything else goes home (a lower-case 'session' still opens a session).
			const fFormConciergeFlow = (pRouteData) =>
			{
				const tmpData = (pRouteData && pRouteData.data) || {};
				const tmpFlow = String(tmpData.Flow || '').toLowerCase();
				if ((tmpFlow === 'session') && tmpData.IDProject)
				{
					fFormConciergeRoute((pProvider) => pProvider.routeApplySession(Number(tmpData.IDProject)));
					return;
				}
				if ([ 'create', 'update', 'examples', 'build' ].indexOf(tmpFlow) < 0)
				{
					this.router.navigate('FormConcierge');
					return;
				}
				fFormConciergeRoute((pProvider) => pProvider.routeApplyFlow(tmpFlow, Number(tmpData.IDProject) || 0));
			};
			// navigo takes the FIRST registered match: Session/:IDRequest must precede :Flow/:IDProject.
			this.pictRouter.addRoute('/FormConcierge/Session/:IDRequest', (pRouteData) => fFormConciergeRoute((pProvider) => pProvider.routeApplySession(Number(pRouteData.data.IDRequest))));
			this.pictRouter.addRoute('/FormConcierge/:Flow/:IDProject', fFormConciergeFlow);
			this.pictRouter.addRoute('/FormConcierge/:Flow', fFormConciergeFlow);
			this.pictRouter.addRoute('/FormConcierge', () => fFormConciergeRoute((pProvider) => pProvider.routeApplyHome()));

			// --- Customer-facing read-only support portal — the Support-View tier's open-cases dashboard. ---
			this.pictRouter.addRoute('/SupportPortal',
				(pRouteData) =>
				{
					this.currentView = 'SupportPortal';
					this._applyDashboardMode(true);   // full-browser-width; warm loadAsync path skips _renderRouteView which would re-apply it
					const tmpProvider = this.pict.providers && this.pict.providers['SupportPortal'];
					let tmpMounted = false;
					try { tmpMounted = !!(this.pict.ContentAssignment && this.pict.ContentAssignment.getElement('#SupportPortal-Host').length > 0); } catch (pError) { /* */ }
					if (tmpProvider && tmpMounted && (typeof tmpProvider.loadAsync === 'function')) { tmpProvider.loadAsync(); }
					else { this._renderRouteView('SupportPortal'); }
				});

			// --- Physical Asset (bridge) dashboard — Map/Browse/Overview over the inventory + backfilled SNBI data. ---
			const fRenderAssetDashboard = () => (pRouteData) =>
			{
				this.currentView = 'PhysicalAssetDashboard';
				const tmpProvider = this.pict.providers && this.pict.providers['PhysicalAssetDashboard'];
				if (tmpProvider && pRouteData && pRouteData.View && (typeof tmpProvider.applyViewFromRoute === 'function')) { tmpProvider.applyViewFromRoute(pRouteData.View); }
				let tmpMounted = false;
				try { tmpMounted = !!(this.pict.ContentAssignment && this.pict.ContentAssignment.getElement('#PAD-Host').length > 0); } catch (pError) { /* */ }
				if (tmpProvider && tmpMounted && (typeof tmpProvider.loadAsync === 'function')) { tmpProvider._paint(); }
				else { this._renderRouteView('PhysicalAssetDashboard'); }
			};
			this.pictRouter.addRoute('/AssetDashboard', fRenderAssetDashboard());
			this.pictRouter.addRoute('/AssetDashboard/:View', fRenderAssetDashboard());

			// The asset-type metadata shape editor (bespoke; gated by AssetTypeManifest-Read).
			this.pictRouter.addRoute('/AssetTypeDescriptor',
			(pRouteData) =>
			{
				this.currentView = 'AssetTypeDescriptor';
				this._renderRouteView('AssetTypeDescriptor');
			});

			// --- Sample configuration screens (bespoke; gated by SampleListColumns-Configure / SampleQRCode-Configure) ---
			this.pictRouter.addRoute('/SampleListColumnsConfig',
			(pRouteData) =>
			{
				this.currentView = 'SampleListColumnsConfig';
				this._renderRouteView('SampleListColumnsConfig');
			});
			this.pictRouter.addRoute('/SampleQRCodeConfig',
			(pRouteData) =>
			{
				this.currentView = 'SampleQRCodeConfig';
				this._renderRouteView('SampleQRCodeConfig');
			});
			// Scan Samples as a page of its own; the LIMS top nav's Scanning Cart links here.
			this.pictRouter.addRoute('/ScanSamples',
			(pRouteData) =>
			{
				this.currentView = 'ScanSamplesPage';
				this._renderRouteView('ScanSamplesPage');
			});
			// Scan Samples action cards; linked from the panels' Settings pane (href #/Printer-Codes), no menu entry.
			this.pictRouter.addRoute('/Printer-Codes',
			(pRouteData) =>
			{
				this.currentView = 'PrinterCodes';
				this._renderRouteView('PrinterCodes');
			});
			this.pictRouter.addRoute('/EntityDestinations',
			(pRouteData) =>
			{
				this.currentView = 'EntityDestinationsConfig';
				this._renderRouteView('EntityDestinationsConfig');
			});
			this.pictRouter.addRoute('/DashboardAccess',
			(pRouteData) =>
			{
				this.currentView = 'DashboardAccessConfig';
				this._renderRouteView('DashboardAccessConfig');
			});
			this.pictRouter.addRoute('/SampleScanningConfig',
			(pRouteData) =>
			{
				this.currentView = 'SampleScanningConfig-View';
				this._renderRouteView('SampleScanningConfig-View');
			});
			this.pictRouter.addRoute('/FormAccess',
			(pRouteData) =>
			{
				this.currentView = 'FormAccessConfig-View';
				this._renderRouteView('FormAccessConfig-View');
			});
			this.pictRouter.addRoute('/FieldbookDashboards',
			(pRouteData) =>
			{
				this.currentView = 'FieldbookDashboardsConfig-View';
				this._renderRouteView('FieldbookDashboardsConfig-View');
			});
			// Role-aware dashboards (headlight-dashboards): /Dashboards opens the person's landing dashboard,
			// /Dashboards/All the list, /Dashboards/:Hash[/Project/:IDProject] one dashboard.
			require('headlight-dashboards').addRoutes(this.pict,
				(pRoute, fHandler) => this.pictRouter.addRoute(pRoute, (pRouteData) =>
				{
					this.currentView = 'HeadlightDashboards';
					fHandler(pRouteData);
				}),
				(pViewIdentifier) => this._renderRouteView(pViewIdentifier));

			// --- Data Explorer (operational-database "folders" tree; surfaced under the Database topic) ---
			this.pictRouter.addRoute('/DataExplorer',
			(pRouteData) =>
			{
				this.currentView = 'DataExplorer';
				this._renderRouteView('DataExplorer');
			});

			// --- Sample Plan (a project's material sampling/testing plan) — lives ON the project as the bespoke
			// ProjectWorkspace Materials ▸ Sample Plan tab; these legacy standalone routes just redirect there
			// (an ID → that project's tab; no ID → the Projects list to pick one). No separate picker screen. ---
			this.pictRouter.addRoute('/SamplePlan',
			() => { window.location.hash = '#/PSRS/Project/List'; });
			this.pictRouter.addRoute('/SamplePlan/:ID',
			(pRouteData) => { window.location.hash = `#/Project/Workspace/${parseInt(pRouteData.data.ID, 10)}/sampleplan`; });

			// Important Project Files — now lives ON the project as the bespoke ProjectWorkspace Data ▸ Important
			// Files tab; these legacy standalone routes just redirect there (an ID → that project's tab; no ID →
			// the Projects list to pick one). No separate picker screen.
			this.pictRouter.addRoute('/ImportantFiles',
			() => { window.location.hash = '#/PSRS/Project/List'; });
			this.pictRouter.addRoute('/ImportantFiles/:ID',
			(pRouteData) => { window.location.hash = `#/Project/Workspace/${parseInt(pRouteData.data.ID, 10)}/data-files`; });

			// The funding dashboard lives ON the project as the bespoke ProjectWorkspace Contract ▸ Funding tab;
			// these legacy standalone routes just redirect there (an ID → that project's tab; no ID → the Projects
			// list to pick one). No separate funding project-picker screen.
			this.pictRouter.addRoute('/Project/Funding',
			() => { window.location.hash = '#/PSRS/Project/List'; });
			this.pictRouter.addRoute('/Project/Funding/:ID',
			(pRouteData) => { window.location.hash = `#/Project/Workspace/${parseInt(pRouteData.data.ID, 10)}/funding`; });

			// --- Configuration viewer/editor (provenance-aware cascading config; under the Database topic) ---
			this.pictRouter.addRoute('/ConfigViewer',
			(pRouteData) =>
			{
				this.currentView = 'ConfigViewer';
				this._renderRouteView('ConfigViewer');
			});

			// --- Documentation manager (in-app help content management, AcademyContent-backed) ---
			this.pictRouter.addRoute('/Documentation',
			(pRouteData) =>
			{
				this.currentView = 'Documentation';
				this._renderRouteView('Documentation');
			});

			// --- Entitlements (task-based feature entitlement over the RBAC system) ---
			this.pictRouter.addRoute('/Entitlements',
			(pRouteData) =>
			{
				this.currentView = 'Entitlements';
				this._renderRouteView('Entitlements');
			});
			// Subject + tab as a stable route (the rail sets this hash via Entitlements.navigateSubject, the tabs via
			// navigateTab), so both the selected Role/Group/User AND its active tab live in the URL. Mount only on a
			// cold arrival; re-select the subject only when it actually changed; then apply the tab — so an in-view
			// tab click is just a setTab and a rail click is a select, neither re-mounting the whole view.
			this.pictRouter.addRoute('/Entitlements/:Kind/:ID/:Tab',
			(pRouteData) =>
			{
				const tmpProvider = this.pict.providers.Entitlements;
				if (!tmpProvider) { return; }
				const tmpValidTabs = [ 'features', 'masterlists', 'contractadmin', 'forms' ];
				const tmpTab = (tmpValidTabs.indexOf(pRouteData.data.Tab) > -1) ? pRouteData.data.Tab : 'features';
				const tmpKind = pRouteData.data.Kind;
				const tmpID = parseInt(pRouteData.data.ID, 10);
				const tmpMounted = (this.currentView === 'Entitlements');
				const tmpSameSubject = tmpMounted && (tmpProvider.state.SubjectKind === tmpKind) && (Number(tmpProvider.state.SubjectID) === tmpID);
				this.currentView = 'Entitlements';
				if (!tmpMounted) { this._renderRouteView('Entitlements'); }
				if (!tmpSameSubject) { tmpProvider.selectSubject(tmpKind, tmpID); }
				tmpProvider.setTab(tmpTab);
			});

			this.pictRouter.addRoute('/Certifications',
			(pRouteData) =>
			{
				this.currentView = 'Certifications';
				this._renderRouteView('Certifications');
			});

			// --- Certification Types (bespoke management: type catalog + per-type config) ---
			this.pictRouter.addRoute('/CertificationTypes',
			(pRouteData) =>
			{
				this.currentView = 'CertificationTypes';
				this._renderRouteView('CertificationTypes');
			});

			// --- Asset Catalog Sync (seed this customer's PhysicalAsset* catalog from the shared public data lake) ---
			this.pictRouter.addRoute('/AssetCatalogSync',
			(pRouteData) =>
			{
				this.currentView = 'AssetCatalogSync';
				this._renderRouteView('AssetCatalogSync');
			});

			// The Asset workspace's read routes (/Asset/Workspace/:ID[/:Tab], /PSRS/PhysicalAsset/View/:GUIDRecord,
			// and the Edit→workspace lock) are now registered by workspace-configs.registerWorkspaceRoutes() above —
			// AssetWorkspace was migrated onto pict-section-workspace (Asset.config.js, Edit:{Route:'workspace'}).
			// Only the PhysicalAsset CREATE lock (externally synced → bounce back to the list) stays hand-written here.
			this.pictRouter.addRoute('/PSRS/PhysicalAsset/Create',
			() =>
			{
				window.location.hash = '#/PSRS/PhysicalAsset/List';
			});

			// The Sample workspace's routes (/Sample/Workspace/:ID[/:Tab], /PSRS/Sample/{View,Edit}/:GUIDRecord, and
			// /PSRS/Sample/Create[/ClonedFrom/:GUIDRecord]) are now registered by workspace-configs.registerWorkspaceRoutes()
			// below — SampleWorkspace was migrated onto pict-section-workspace (Sample.config.js; Edit + Create modes).

			// --- Config-driven Headlight Workspaces: register each one's GUID-bridge + /:Tab routes ---
			require('../workspace-configs').registerWorkspaceRoutes(this);

			// --- Project Types / Contract Types (bespoke editors: definition + statuses + dates + prototype) ---
			this.pictRouter.addRoute('/ProjectTypes',
			(pRouteData) =>
			{
				this.currentView = 'ProjectTypes';
				this._renderRouteView('ProjectTypes');
			});
			this.pictRouter.addRoute('/ProjectTypes/:ID',
			(pRouteData) =>
			{
				this.currentView = 'ProjectTypes';
				this._renderRouteView('ProjectTypes');
				this.pict.providers.ProjectContractTypes.openType('Project', pRouteData.data.ID);
			});
			this.pictRouter.addRoute('/ContractTypes',
			(pRouteData) =>
			{
				this.currentView = 'ContractTypes';
				this._renderRouteView('ContractTypes');
			});
			this.pictRouter.addRoute('/ContractTypes/:ID',
			(pRouteData) =>
			{
				this.currentView = 'ContractTypes';
				this._renderRouteView('ContractTypes');
				this.pict.providers.ProjectContractTypes.openType('Contract', pRouteData.data.ID);
			});

			// --- Important dates editors (ProjectDate / ContractDate) ---
			this.pictRouter.addRoute('/ProjectDates/:ID',
			(pRouteData) =>
			{
				this.currentView = 'RecordDates';
				this._renderRouteView('RecordDates');
				this.pict.providers.RecordDates.openAsync('Project', parseInt(pRouteData.data.ID, 10));
			});
			this.pictRouter.addRoute('/ContractDates/:ID',
			(pRouteData) =>
			{
				this.currentView = 'RecordDates';
				this._renderRouteView('RecordDates');
				this.pict.providers.RecordDates.openAsync('Contract', parseInt(pRouteData.data.ID, 10));
			});

			// --- Grant Access (super-user bootstrap: give a user Permission-Grant) ---
			this.pictRouter.addRoute('/GrantAccess',
			(pRouteData) =>
			{
				this.currentView = 'SuperUserGrant';
				this._renderRouteView('SuperUserGrant');
			});
			// Deep link that pre-scopes Grant Access to one customer (from the Customers screen's "grant one in
			// Grants first" action) — renders the same screen, then selects that customer in its filter.
			this.pictRouter.addRoute('/GrantAccess/:IDCustomer',
			(pRouteData) =>
			{
				this.currentView = 'SuperUserGrant';
				this._renderRouteView('SuperUserGrant');
				const tmpProvider = this.pict.providers.SuperUserGrant;
				const tmpIDCustomer = parseInt(pRouteData.data.IDCustomer, 10);
				if (tmpProvider && (typeof tmpProvider.applyCustomerScope === 'function') && tmpIDCustomer) { tmpProvider.applyCustomerScope(tmpIDCustomer); }
			});

			// --- Customers (front door: the customer roster → per-customer Customer workspace; plus a New Customers
			// tab listing provisioning-request documents). Both tabs share the Customers view + CustomersList provider;
			// the route just flips the provider's mode (a stable, linkable tab per the Support pattern).
			const fCustomersRoute = (pMode) =>
			{
				const tmpProvider = this.pict.providers.CustomersList;
				const tmpAlreadyMounted = (this.currentView === 'Customers');
				this.currentView = 'Customers';
				if (!tmpAlreadyMounted) { this._renderRouteView('Customers'); }
				if (tmpProvider && (typeof tmpProvider.setMode === 'function')) { tmpProvider.setMode(pMode); }
			};
			this.pictRouter.addRoute('/Customers', () => fCustomersRoute('roster'));
			this.pictRouter.addRoute('/Customers/New', () => fCustomersRoute('new'));
			// --- Manage Customers (RETIRED) — redirect to the new Customers list. The old screen's Modules + Customer
			// Team tooling moved into the Customer workspace; impersonation is the per-row "Manage Configuration" action.
			const fRedirectToCustomers = () => { if ((typeof window !== 'undefined') && window.location) { window.location.hash = '#/Customers'; } };
			this.pictRouter.addRoute('/ManageCustomers', fRedirectToCustomers);
			this.pictRouter.addRoute('/ManageCustomers/:Mode', fRedirectToCustomers);

			// --- Headlight Academy (full-page documentation browser; reuses the inline-doc pipeline) ---
			const fAcademyRoute = (pTopic) =>
			{
				const tmpAlreadyMounted = (this.currentView === 'Academy');
				this.currentView = 'Academy';
				const tmpProvider = this.pict.providers.Academy;
				// Set the target BEFORE rendering so the view's onViewRendered opens the requested article, not the
				// overview default; when already mounted, swap it in place with no re-render.
				if (tmpProvider) { tmpProvider.prepare(pTopic); }
				if (!tmpAlreadyMounted) { this._renderRouteView('Academy'); }
				else if (tmpProvider) { tmpProvider.showTargetIfMounted(); }
			};
			this.pictRouter.addRoute('/Academy', () => fAcademyRoute(null));
			this.pictRouter.addRoute('/Academy/:Topic', (pRouteData) => fAcademyRoute(pRouteData.data.Topic));

			// --- Activity observability (bespoke): per-user activity, user logins, per-project activity ---
			this.pictRouter.addRoute('/UserActivity',
			(pRouteData) =>
			{
				this.currentView = 'UserActivity';
				this._renderRouteView('UserActivity');
			});
			this.pictRouter.addRoute('/UserActivity/:IDUser',
			(pRouteData) =>
			{
				this.currentView = 'UserActivity';
				// Pre-set the target so the view's onAfterRender auto-load stands down, then load explicitly.
				this.pict.providers.ActivityObservability.userActivity.IDUser = parseInt(pRouteData.data.IDUser, 10);
				this._renderRouteView('UserActivity');
				this.pict.providers.ActivityObservability.loadUserActivityAsync(parseInt(pRouteData.data.IDUser, 10));
			});
			this.pictRouter.addRoute('/UserLoginOverview',
			(pRouteData) =>
			{
				this.currentView = 'UserLoginOverview';
				this._renderRouteView('UserLoginOverview');
			});
			this.pictRouter.addRoute('/ProjectActivity',
			(pRouteData) =>
			{
				if (this.pict.views.ProjectActivity && (typeof this.pict.views.ProjectActivity.exitEmbed === 'function')) { this.pict.views.ProjectActivity.exitEmbed(); }
				this.currentView = 'ProjectActivity';
				this._renderRouteView('ProjectActivity');
			});
			this.pictRouter.addRoute('/ProjectActivity/:IDProject',
			(pRouteData) =>
			{
				if (this.pict.views.ProjectActivity && (typeof this.pict.views.ProjectActivity.exitEmbed === 'function')) { this.pict.views.ProjectActivity.exitEmbed(); }
				this.currentView = 'ProjectActivity';
				this.pict.providers.ActivityObservability.projectActivity.IDProject = parseInt(pRouteData.data.IDProject, 10);
				this._renderRouteView('ProjectActivity');
				this.pict.providers.ActivityObservability.loadProjectActivityAsync(parseInt(pRouteData.data.IDProject, 10));
			});

			// --- Clone configuration across contexts ---
			this.pictRouter.addRoute('/ConfigClone',
			(pRouteData) =>
			{
				this.currentView = 'ConfigClone';
				this._renderRouteView('ConfigClone');
			});

			// --- Clone a role (deep-copy grants + edges into a new, renamed role) ---
			this.pictRouter.addRoute('/RoleClone',
			(pRouteData) =>
			{
				this.currentView = 'RoleClone';
				this._renderRouteView('RoleClone');
			});
			// Deep-link with a preselected source role (from the Access "Duplicate" action).
			this.pictRouter.addRoute('/RoleClone/:IDRole',
			(pRouteData) =>
			{
				this.currentView = 'RoleClone';
				this._renderRouteView('RoleClone');
				const tmpProvider = this.pict.providers.RoleCloneProvider;
				if (tmpProvider && pRouteData && pRouteData.data && pRouteData.data.IDRole)
				{
					tmpProvider.preselectSourceAsync(pRouteData.data.IDRole).then(() =>
					{
						if (this.pict.views.RoleClone) { this.pict.views.RoleClone.render(); }
					});
				}
			});

			// --- Data Mapper (pict-section-mapping; under the Data Lake topic) ---
			this.pictRouter.addRoute('/DataMapper',
			(pRouteData) =>
			{
				this.currentView = 'DataMapper';
				this._renderRouteView('DataMapper');
			});

			// --- Data Import (pict-section-dataimport; generic reference-entity import, under Master Lists) ---
			this.pictRouter.addRoute('/DataImport',
			(pRouteData) =>
			{
				this.currentView = 'DataImport';
				this._renderRouteView('DataImport');
			});

			// --- Contract Package Import (bespoke Contract → Project → Bid Item → Line Item importer) ---
			this.pictRouter.addRoute('/ContractImport',
			(pRouteData) =>
			{
				if (this.pict.providers.ContractPackageImportManager) { this.pict.providers.ContractPackageImportManager.setFlowQuiet('package'); }
				this.currentView = 'ContractPackageImport';
				this._renderRouteView('ContractPackageImport');
			});

			// --- Import Line Items (same view, 'items' flow: add Bid/Line Items to existing Projects) ---
			this.pictRouter.addRoute('/ItemImport',
			(pRouteData) =>
			{
				if (this.pict.providers.ContractPackageImportManager) { this.pict.providers.ContractPackageImportManager.setFlowQuiet('items'); }
				this.currentView = 'ContractPackageImport';
				this._renderRouteView('ContractPackageImport');
			});

			// --- Access & Permissions workspace (RBAC: roles, permission groups, matrix, assignment) ---
			this.pictRouter.addRoute('/Access',
			(pRouteData) =>
			{
				this.currentView = 'AccessWorkspace';
				this._renderRouteView('AccessWorkspace');
				this.pict.providers.AccessWorkspace.loadAsync();
			});

			this.pictRouter.addRoute('/Access/Role/:ID',
			(pRouteData) =>
			{
				this.currentView = 'AccessWorkspace';
				this._renderRouteView('AccessWorkspace');
				this.pict.providers.AccessWorkspace.loadAsync();
				this.pict.providers.AccessWorkspace.selectRoleAsync(pRouteData.data.ID);
			});

			this.pictRouter.addRoute('/Access/Group/:ID',
			(pRouteData) =>
			{
				this.currentView = 'AccessWorkspace';
				this._renderRouteView('AccessWorkspace');
				this.pict.providers.AccessWorkspace.loadAsync();
				this.pict.providers.AccessWorkspace.selectGroupAsync(pRouteData.data.ID);
			});

			this.pictRouter.addRoute('/Access/User/:ID',
			(pRouteData) =>
			{
				this.currentView = 'AccessWorkspace';
				this._renderRouteView('AccessWorkspace');
				this.pict.providers.AccessWorkspace.loadAsync();
				this.pict.providers.AccessWorkspace.selectUserAsync(pRouteData.data.ID);
			});

			// Subject + tab as a stable route (the tab buttons set this hash via AccessWorkspace.navigateTab), so
			// both the selected role/group/user AND its active detail tab live in the URL. When we're already on
			// that same subject the tab switch is a cheap setActiveTab (no re-select/re-fetch); otherwise the
			// subject is selected first, then the requested tab applied (selectXAsync lands on 'permissions', so the
			// tab is set after it resolves). One handler builder for the three subject kinds.
			const fAccessTabRoute = (pKind, pSelectMethod) =>
			{
				this.pictRouter.addRoute(`/Access/${ pKind }/:ID/:Tab`,
				(pRouteData) =>
				{
					const tmpProvider = this.pict.providers.AccessWorkspace;
					if (!tmpProvider) { return; }
					const tmpValidTabs = [ 'details', 'permissions', 'effective', 'composition', 'members', 'assignment', 'overrides', 'projectsgranted' ];
					const tmpTab = (tmpValidTabs.indexOf(pRouteData.data.Tab) > -1) ? pRouteData.data.Tab : 'details';
					const tmpID = parseInt(pRouteData.data.ID, 10);
					const tmpKind = pKind.toLowerCase();
					const tmpAlready = (this.currentView === 'AccessWorkspace') && (tmpProvider.state.SubjectKind === tmpKind) && (Number(tmpProvider.state.SubjectID) === tmpID);
					this.currentView = 'AccessWorkspace';
					if (tmpAlready) { tmpProvider.setActiveTab(tmpTab); return; }
					this._renderRouteView('AccessWorkspace');
					tmpProvider.loadAsync();
					Promise.resolve(tmpProvider[pSelectMethod](tmpID)).then(() => tmpProvider.setActiveTab(tmpTab));
				});
			};
			fAccessTabRoute('Role', 'selectRoleAsync');
			fAccessTabRoute('Group', 'selectGroupAsync');
			fAccessTabRoute('User', 'selectUserAsync');

			// Bridge the raw Role list's "+ New" into the cohesive Access workspace in create-role mode
			// (instead of the generic manifest form). Registered inside this literal block so it wins over the
			// generic /PSRS/:RecordSet/Create on the shared Navigo. newRole() (_startNew) is safe cold; loadAsync()
			// paints the left rail (it does not clobber the staged new-role subject).
			this.pictRouter.addRoute('/PSRS/Role/Create',
			() =>
			{
				this.currentView = 'AccessWorkspace';
				this._renderRouteView('AccessWorkspace');
				this.pict.providers.AccessWorkspace.loadAsync();
				this.pict.providers.AccessWorkspace.newRole();
			});

			// Bridge the raw Role list's read route into the cohesive Access workspace.
			this.pictRouter.addRoute('/PSRS/Role/View/:GUIDRecord',
			(pRouteData) =>
			{
				this.currentView = 'AccessWorkspace';
				this._renderRouteView('AccessWorkspace');
				this.pict.providers.AccessWorkspace.openRoleByGUIDAsync(pRouteData.data.GUIDRecord);
			});

			// --- Data Lake (materialized projections): catalog landing + dataset viewer + create wizard ---
			this.pictRouter.addRoute('/Datalake',
			(pRouteData) =>
			{
				this.currentView = 'DatalakeCatalog';
				this._renderRouteView('DatalakeCatalog');
				this.pict.providers.DatalakeCatalogProvider.loadAsync();
			});

			// Data Downloader: the gated desktop-app download page (literal segment near the other /Datalake/*
			// literals; no /Datalake/:param catch-all exists so ordering is unconstrained).
			this.pictRouter.addRoute('/Datalake/DataDownloader',
			(pRouteData) =>
			{
				this.currentView = 'DataDownloader';
				this._renderRouteView('DataDownloader');
				this.pict.providers.DataDownloaderProvider.loadAsync();
			});

			this.pictRouter.addRoute('/Datalake/New',
			(pRouteData) =>
			{
				this.currentView = 'DatalakeProjectionEditor';
				this._renderRouteView('DatalakeProjectionEditor');
				this.pict.providers.DatalakeProjectionEditorProvider.newProjection();
			});

			this.pictRouter.addRoute('/Datalake/Dataset/:GUIDRecord',
			(pRouteData) =>
			{
				this.currentView = 'DatalakeDataset';
				this._renderRouteView('DatalakeDataset');
				this.pict.providers.DatalakeDatasetProvider.openByGUIDAsync(pRouteData.data.GUIDRecord);
			});

			// Raw imported lake table (a clone table with no MaterializedProjection row), opened by physical name.
			this.pictRouter.addRoute('/Datalake/Table/:TableName',
			(pRouteData) =>
			{
				this.currentView = 'DatalakeDataset';
				this._renderRouteView('DatalakeDataset');
				this.pict.providers.DatalakeDatasetProvider.openByTableAsync(decodeURIComponent(pRouteData.data.TableName));
			});
			// Catalog kind (Projections / Imported) as a stable route (rail links set this hash via
			// DatalakeCatalogProvider.navigateKind). A distinct `/Kind/` segment so it never shadows the literal
			// /Datalake/New | /Datalake/Dataset/* | /Datalake/Table/* routes above. setKind is a cheap re-render.
			this.pictRouter.addRoute('/Datalake/Kind/:Kind',
			(pRouteData) =>
			{
				const tmpProvider = this.pict.providers.DatalakeCatalogProvider;
				const tmpKind = (pRouteData.data.Kind === 'imported') ? 'imported' : 'computed';
				const tmpAlreadyMounted = (this.currentView === 'DatalakeCatalog');
				this.currentView = 'DatalakeCatalog';
				if (!tmpAlreadyMounted) { this._renderRouteView('DatalakeCatalog'); if (tmpProvider) { tmpProvider.loadAsync(); } }
				if (tmpProvider) { tmpProvider.setKind(tmpKind); }
			});

			// --- Approval-workflow visual editor (list + graph over the 'ApprovalWorkflow' Config blob).
			// 'New' before the :Hash catch-all so it isn't read as a workflow hash. ---
			this.pictRouter.addRoute('/ApprovalWorkflows',
			(pRouteData) =>
			{
				this.currentView = 'ApprovalWorkflowList';
				this._renderRouteView('ApprovalWorkflowList');
				this.pict.providers.ApprovalWorkflow.loadListAsync();
			});

			// Document bindings — before the :Hash catch-all so 'Bindings' isn't read as a workflow hash.
			// Observation bindings — a second bindings page over the SAME shared workflow pool (binds observation
			// types instead of document types). Registered before the bare /Bindings route.
			this.pictRouter.addRoute('/ApprovalWorkflows/Bindings/Observation',
			(pRouteData) =>
			{
				this.currentView = 'ApprovalWorkflowBindings';
				this._renderRouteView('ApprovalWorkflowBindings');
				this.pict.providers.ApprovalWorkflow.loadBindingsAsync('Observation');
			});

			this.pictRouter.addRoute('/ApprovalWorkflows/Bindings',
			(pRouteData) =>
			{
				this.currentView = 'ApprovalWorkflowBindings';
				this._renderRouteView('ApprovalWorkflowBindings');
				this.pict.providers.ApprovalWorkflow.loadBindingsAsync('Document');
			});

			this.pictRouter.addRoute('/ApprovalWorkflow/New',
			(pRouteData) =>
			{
				this.currentView = 'ApprovalWorkflowEditor';
				this._renderRouteView('ApprovalWorkflowEditor');
				this.pict.providers.ApprovalWorkflow.newWorkflowAsync();
			});

			this.pictRouter.addRoute('/ApprovalWorkflow/:Hash/Project/:IDProject',
			(pRouteData) =>
			{
				this.currentView = 'ApprovalWorkflowEditor';
				this._renderRouteView('ApprovalWorkflowEditor');
				this.pict.providers.ApprovalWorkflow.loadWorkflowAsync(decodeURIComponent(pRouteData.data.Hash), parseInt(pRouteData.data.IDProject, 10) || 0);
			});

			this.pictRouter.addRoute('/ApprovalWorkflow/:Hash',
			(pRouteData) =>
			{
				this.currentView = 'ApprovalWorkflowEditor';
				this._renderRouteView('ApprovalWorkflowEditor');
				this.pict.providers.ApprovalWorkflow.loadWorkflowAsync(decodeURIComponent(pRouteData.data.Hash));
			});

			// --- Email Notifications (Email Rules) — migrated from Home's EmailRules.vue. List now; the
			// #/EmailRule/New and #/EmailRule/:ID editor routes land in the editor phase. ---
			this.pictRouter.addRoute('/EmailNotifications',
			(pRouteData) =>
			{
				this.currentView = 'EmailNotificationsList';
				this._renderRouteView('EmailNotificationsList');
				this.pict.providers.EmailNotifications.loadListAsync();
			});

			// New before the :ID catch-all so 'New' isn't read as a rule id.
			this.pictRouter.addRoute('/EmailRule/New',
			(pRouteData) =>
			{
				this.currentView = 'EmailNotificationsEditor';
				this._renderRouteView('EmailNotificationsEditor');
				this.pict.providers.EmailNotifications.newRuleAsync();
			});

			this.pictRouter.addRoute('/EmailRule/:ID',
			(pRouteData) =>
			{
				this.currentView = 'EmailNotificationsEditor';
				this._renderRouteView('EmailNotificationsEditor');
				this.pict.providers.EmailNotifications.loadRuleAsync(decodeURIComponent(pRouteData.data.ID));
			});

			// --- Observation Type builder (bespoke record editor; the pict-section-recordset list
			// stays the browse surface). New / Clone before the :ID catch-all so they aren't read as ids. ---
			this.pictRouter.addRoute('/ObservationType/New',
			(pRouteData) =>
			{
				this.currentView = 'ObservationTypeEditor';
				this._renderRouteView('ObservationTypeEditor');
				this.pict.providers.ObservationTypeEditor.newType();
			});

			this.pictRouter.addRoute('/ObservationType/Clone/:ID',
			(pRouteData) =>
			{
				this.currentView = 'ObservationTypeEditor';
				this._renderRouteView('ObservationTypeEditor');
				this.pict.providers.ObservationTypeEditor.cloneFrom(parseInt(pRouteData.data.ID, 10), this.pict.providers.ObservationTypeEditor.idCustomer);
			});

			this.pictRouter.addRoute('/ObservationType/:ID',
			(pRouteData) =>
			{
				this.currentView = 'ObservationTypeEditor';
				this._renderRouteView('ObservationTypeEditor');
				this.pict.providers.ObservationTypeEditor.loadAsync(parseInt(pRouteData.data.ID, 10));
			});

			// Bridge: the recordset list links to a record by GUID; intercept the ObservationManifest
			// read route and open our bespoke editor (resolving GUID -> real ID) instead of the auto form.
			this.pictRouter.addRoute('/PSRS/ObservationManifest/View/:GUIDRecord',
			(pRouteData) =>
			{
				this.currentView = 'ObservationTypeEditor';
				this._renderRouteView('ObservationTypeEditor');
				this.pict.providers.ObservationTypeEditor.openByGUIDAsync(pRouteData.data.GUIDRecord);
			});

			// Bridge: the "+ New" on the ObservationManifest recordset list shadows the generic create form.
			// Redirect to the bespoke Observation Type builder (ObservationTypeEditor.newType via /ObservationType/New).
			// Registered before the generic /PSRS/:RecordSet/Create so this literal wins on the shared Navigo.
			this.pictRouter.addRoute('/PSRS/ObservationManifest/Create',
			() =>
			{
				window.location.hash = '#/ObservationType/New';
			});

			// The Project workspace's read routes (/Project/Workspace/:ID[/:Tab] + /PSRS/Project/View/:GUIDRecord)
			// are now registered by workspace-configs.registerWorkspaceRoutes() above — ProjectWorkspace was migrated
			// onto pict-section-workspace (Project.config.js). The bespoke Edit + Create bridges stay hand-written here.

			// Bridge: intercept the Project EDIT route (shadows the generic recordset edit form) and open the
			// bespoke edit workspace, which locks the Financial & Schedule fields once the project is Active.
			this.pictRouter.addRoute('/PSRS/Project/Edit/:GUIDRecord',
			(pRouteData) =>
			{
				this.currentView = 'ProjectEditWorkspace';
				this._renderRouteView('ProjectEditWorkspace');
				this.pict.providers.ProjectEditWorkspace.openByGUIDAsync(pRouteData.data.GUIDRecord);
			});

			// Bridge: intercept the Project CREATE route (the "+ New" button, which shadows the generic recordset
			// create form) and open the bespoke create workspace instead of the plain manifest form. Registered
			// before the generic /PSRS/:RecordSet/Create so this literal wins on the shared Navigo.
			this.pictRouter.addRoute('/PSRS/Project/Create',
			() =>
			{
				this.currentView = 'ProjectCreateWorkspace';
				this._renderRouteView('ProjectCreateWorkspace');
				this.pict.providers.ProjectCreateWorkspace.startNew();
			});

			// The Contract workspace's read routes (/Contract/Workspace/:ID[/:Tab] + /PSRS/Contract/View/:GUIDRecord)
			// are now registered by workspace-configs.registerWorkspaceRoutes() above — ContractWorkspace was
			// migrated onto pict-section-workspace. Only the bespoke CREATE bridge stays hand-written here.

			// Bridge: intercept the Contract CREATE route (the "+ New" button, which shadows the generic recordset
			// create form) and open the bespoke create workspace instead of the plain manifest form. Registered
			// before the generic /PSRS/:RecordSet/Create so this literal wins on the shared Navigo.
			this.pictRouter.addRoute('/PSRS/Contract/Create',
			() =>
			{
				this.currentView = 'ContractCreateWorkspace';
				this._renderRouteView('ContractCreateWorkspace');
				this.pict.providers.ContractCreateWorkspace.startNew();
			});

			// Bridge: intercept the Inspection CREATE route so "+ New" opens the bespoke schedule screen rather
			// than the generic manifest form. This one is not cosmetic — the generic form POSTs a lone Inspection
			// row with no asset join, no project and no document packet, which no screen in the platform can then
			// show properly. Registered before the generic /PSRS/:RecordSet/Create so this literal wins.
			// /PSRS/Inspection/Create/ForAsset/:IDPhysicalAsset pre-selects the subject — that is how the Asset
			// workspace's "Schedule inspection" action arrives here.
			this.pictRouter.addRoute('/PSRS/Inspection/Create',
			() =>
			{
				this.currentView = 'InspectionCreateWorkspace';
				this._renderRouteView('InspectionCreateWorkspace');
				this.pict.providers.InspectionCreateWorkspace.startNewAsync();
			});
			this.pictRouter.addRoute('/PSRS/Inspection/Create/ForAsset/:IDPhysicalAsset',
			(pRouteData) =>
			{
				this.currentView = 'InspectionCreateWorkspace';
				this._renderRouteView('InspectionCreateWorkspace');
				this.pict.providers.InspectionCreateWorkspace.startNewAsync(pRouteData?.data?.IDPhysicalAsset);
			});

			// The User workspace's read routes (/User/Workspace/:ID[/:Tab] + /PSRS/User/View/:GUIDRecord) are now
			// registered by workspace-configs.registerWorkspaceRoutes() above — UserWorkspace was migrated onto
			// pict-section-workspace (User.config.js; adds an Activity tab). Only the bespoke CREATE bridge stays here.

			// Bridge: intercept the User CREATE route (the "+ New" button, which shadows the generic recordset
			// create form) and open the bespoke create workspace instead of the plain manifest form (which exposed
			// raw columns like Password Reset Key). Registered before the generic /PSRS/:RecordSet/Create.
			this.pictRouter.addRoute('/PSRS/User/Create',
			() =>
			{
				this.currentView = 'UserCreateWorkspace';
				this._renderRouteView('UserCreateWorkspace');
				this.pict.providers.UserCreateWorkspace.startNew();
			});

			// --- Error Log Viewer (read-only diagnostics — recent HLWebErrorLog artifacts) ---
			this.pictRouter.addRoute('/ErrorLog',
			() =>
			{
				this.currentView = 'ErrorLogViewer';
				this._renderRouteView('ErrorLogViewer');
				this.pict.providers.ErrorLogViewer.loadAsync();
			});

			// --- Report -> Project assignment (shared headlight-report-project-assignment view) ---
			this.pictRouter.addRoute('/ReportProjectAssignment',
			() =>
			{
				this.currentView = 'ReportNamedInstanceProjectForms';
				// The view owns its render + data load via onNavigate (mirrors the form editor's /ProjectForms).
				this.pict.views.ReportNamedInstanceProjectForms.onNavigate();
			});

			// --- My Headlight (the organization's own config landing — customer logo, branding) ---
			this.pictRouter.addRoute('/MyHeadlight',
			() =>
			{
				this.currentView = 'MyHeadlight';
				this._renderRouteView('MyHeadlight');
				this.pict.providers.MyHeadlight.loadAsync();
			});

			// --- Artifact Type builder (defines the fields/columns an observation collects) ---
			this.pictRouter.addRoute('/ArtifactType/New',
			(pRouteData) =>
			{
				this.currentView = 'ArtifactTypeEditor';
				this._renderRouteView('ArtifactTypeEditor');
				this.pict.providers.ArtifactTypeEditor.newType();
			});

			this.pictRouter.addRoute('/ArtifactType/:ID',
			(pRouteData) =>
			{
				this.currentView = 'ArtifactTypeEditor';
				this._renderRouteView('ArtifactTypeEditor');
				this.pict.providers.ArtifactTypeEditor.loadAsync(parseInt(pRouteData.data.ID, 10));
			});

			this.pictRouter.addRoute('/PSRS/ArtifactType/View/:GUIDRecord',
			(pRouteData) =>
			{
				this.currentView = 'ArtifactTypeEditor';
				this._renderRouteView('ArtifactTypeEditor');
				this.pict.providers.ArtifactTypeEditor.openByGUIDAsync(pRouteData.data.GUIDRecord);
			});

			// Bridge: the "+ New" on the ArtifactType recordset list shadows the generic create form.
			// Redirect to the bespoke Artifact Type builder (ArtifactTypeEditor.newType via /ArtifactType/New).
			// Registered before the generic /PSRS/:RecordSet/Create so this literal wins on the shared Navigo.
			this.pictRouter.addRoute('/PSRS/ArtifactType/Create',
			() =>
			{
				window.location.hash = '#/ArtifactType/New';
			});

			// (The Analytics dashboards were retired — the whole #/Analytics/* route set, its views, and its nav
			// entries were removed. ObservationExplorer / the Activity views cover the live reporting surface.)

			return fCallback(pError);
		});
	}
}

module.exports = PlatformConfigurationRouter;

module.exports.default_configuration = (
	{
		Name: 'PlatformConfigurationRouter',
	});

/**
 * Dashboard Mode CSS — a reusable full-browser-width layout any top-level view opts into via `DashboardMode: true`.
 * The router toggles `.is-dashboard` on the app content wrapper (see _applyDashboardMode); this drops the standard
 * side gutters and lifts the Bulma `.container` max-width so the view fills the whole browser width, edge to edge.
 */
const _DASHBOARD_MODE_CSS = /*css*/`
	.app-wrapper-container.is-dashboard { padding-left: 0; padding-right: 0; }
	.app-wrapper-container.is-dashboard > .container,
	.app-wrapper-container.is-dashboard #PlatformConfiguration-Application-Container { max-width: none; width: 100%; }
`;
